import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Container,
  Autocomplete,
  TextField,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Alert,
  AlertTitle,
  InputAdornment,
  CssBaseline,
  ThemeProvider,
  createTheme
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ScienceIcon from '@mui/icons-material/Science';

import supabase from "../../utils/supabase";
import useDebouncedSearch from "../../utils/useDebouncedSearch";
import TaxonomicTree from "../Dialogs/TaxonomicTree";
import DialogDisplayScientificNameDetails from "../Dialogs/DialogDisplayScientificNameDetails/DialogDisplayScientificNameDetails";
// 全件ロードを避けるため、必要なノードを遅延取得する
const MIN_FIELDS = "id,name_spell_valid,name_spell_original,current_rank,current_parent,original_parent,authority_year,valid_name_id";
const AUTHOR_JOIN = "scientific_name_and_author!left(author_order,researcher:researcher_id(id,first_name,last_name))";
const BASE_SELECT = `${MIN_FIELDS},${AUTHOR_JOIN}`;
const CHILD_PAGE_SIZE = 50;

// --- ダークテーマ定義 ---
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#7fffd4' },
    background: { default: '#000000', paper: '#1a1a1a' },
    text: { primary: '#e0e0e0', secondary: '#b0bec5' },
    action: { active: '#7fffd4' },
  },
  typography: {
    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
    h3: { fontWeight: 700 },
    h6: { fontWeight: 600 },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid rgba(127,255,212,0.3)',
          borderRadius: '12px',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          border: '1px solid rgba(127,255,212,0.5)',
          borderRadius: '8px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        outlined: {
          borderColor: 'rgba(127,255,212,0.5)',
          color: '#7fffd4',
          '&:hover': {
            backgroundColor: 'rgba(127,255,212,0.1)',
            borderColor: '#7fffd4',
          }
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '16px',
          border: '1px solid rgba(127,255,212,0.4)',
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(127,255,212,0.2)',
        }
      }
    }
  },
});

export default function Taxonomy() {
  const [allScientificNames, setAllScientificNames] = useState([]);
  const [scientificNameAuthors, setScientificNameAuthors] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);

  const [initializing, setInitializing] = useState(true);
  const [initialLoadError, setInitialLoadError] = useState(null);
  const [selectedScientificName, setSelectedScientificName] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // サジェストとロード状況
  const [searchInput, setSearchInput] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);
  const [suggestionLoading, setSuggestionLoading] = useState(false);

  // 子ノードのフェッチ済み管理
  const [fetchedChildren, setFetchedChildren] = useState(new Map()); // parentId -> last loaded page (0-index)
  const [loadingChildren, setLoadingChildren] = useState(new Set());
  const [childrenTotals, setChildrenTotals] = useState({}); // parentId -> total children count
  const [expandedNodes, setExpandedNodes] = useState({});
  const [persistExpanded, setPersistExpanded] = useState(new Set());
  const [descendantCountMap, setDescendantCountMap] = useState({}); // parentId -> { valid: {rank: total}, invalid: {status: total} }
  const descendantCountPendingRef = useRef(new Set());
  const descendantCountMapRef = useRef(descendantCountMap);

  useEffect(() => {
    descendantCountMapRef.current = descendantCountMap;
  }, [descendantCountMap]);

  const normalizeDescendantCounts = useCallback((rows = []) => {
    const result = { valid: {}, invalid: {} };
    (rows || []).forEach((r) => {
      const key = (r.rank || r.status || r.key || "").toLowerCase();
      const total = r.total ?? r.count ?? 0;
      const kind = (r.kind || r.type || "").toLowerCase();
      if (!key) return;
      const target =
        kind === "invalid" || key === "synonym" || key === "homonym" || key === "invalid"
          ? "invalid"
          : "valid";
      result[target][key] = total;
    });
    if (!Object.keys(result.valid).length && !Object.keys(result.invalid).length && rows.length) {
      rows.forEach((r) => {
        const key = (r.rank || "").toLowerCase();
        const total = r.total ?? 0;
        if (key) result.valid[key] = total;
      });
    }
    return result;
  }, []);

  const resultsContainerRef = useRef(null);

  const mergeNodes = useCallback((nodes) => {
    if (!Array.isArray(nodes) || nodes.length === 0) return;
    setAllScientificNames((prev) => {
      const map = new Map(prev.map((n) => [n.id, n]));
      nodes.forEach((n) => {
        if (n?.id) map.set(n.id, { ...map.get(n.id), ...n });
      });
      return Array.from(map.values());
    });
  }, []);

  const mergeAuthors = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    setScientificNameAuthors((prev) => {
      const map = new Map(prev.map((r) => [r.id, r]));
      rows.forEach((r) => {
        if (r?.id) map.set(r.id, r);
      });
      return Array.from(map.values());
    });
  }, []);

  const mergeAuthorsFromRows = useCallback((rows = []) => {
    const rels = [];
    rows.forEach((row) => {
      if (!row?.id || !Array.isArray(row.scientific_name_and_author)) return;
      row.scientific_name_and_author.forEach((rel) => {
        rels.push({
          ...rel,
          id: `${row.id}-${rel.author_order ?? 0}-${rel.researcher?.id ?? ""}`,
          scientific_name_id: row.id,
        });
      });
    });
    if (rels.length) mergeAuthors(rels);
  }, [mergeAuthors]);

  const fetchDescendantCounts = useCallback(async (id) => {
    if (!id) return;
    if (descendantCountMapRef.current[id]) return;
    if (descendantCountPendingRef.current.has(id)) return;
    descendantCountPendingRef.current.add(id);
    try {
      const { data, error } = await supabase.rpc("taxonomy_descendant_counts", { p_id: id });
      if (error) throw error;
      const normalized = normalizeDescendantCounts(data || []);
      setDescendantCountMap((prev) => ({ ...prev, [id]: normalized }));
    } catch (e) {
      console.warn("[Taxonomy] fetchDescendantCounts error", e.message || e);
    } finally {
      descendantCountPendingRef.current.delete(id);
    }
  }, [normalizeDescendantCounts]);

  const ensureDescendantCounts = useCallback(
    async (ids = []) => {
      const uniq = Array.from(new Set(ids.filter(Boolean)));
      if (!uniq.length) return;
      await Promise.all(uniq.map((cid) => fetchDescendantCounts(cid)));
    },
    [fetchDescendantCounts]
  );

  const fetchAuthorsForIds = useCallback(async (ids) => {
    const uniq = Array.from(new Set((ids || []).filter(Boolean)));
    if (!uniq.length) return;
    const { data, error } = await supabase
      .from("scientific_name_and_author")
      .select("*, researchers:researcher_id (id, first_name, last_name)")
      .in("scientific_name_id", uniq);
    if (error) {
      console.warn("[Taxonomy] fetchAuthors error:", error.message);
      return;
    }
    mergeAuthors(data || []);
  }, [mergeAuthors]);

  const fetchNodeById = useCallback(async (id) => {
    if (!id) return null;
    const { data, error } = await supabase
      .from("scientific_names")
      .select(BASE_SELECT)
      .eq("id", id)
      .maybeSingle();
    if (error) {
      console.warn("[Taxonomy] fetchNodeById error:", error.message);
      return null;
    }
    if (data) {
      mergeNodes([data]);
      mergeAuthorsFromRows([data]);
    }
    return data;
  }, [mergeNodes, mergeAuthorsFromRows]);

  const fetchChildren = useCallback(
    async (parentId, page = 0) => {
      if (!parentId) return;
      const lastLoaded = fetchedChildren.get(parentId);
      if (typeof lastLoaded === "number" && page <= lastLoaded) return;
      setLoadingChildren((prev) => {
        if (prev.has(parentId)) return prev;
        const next = new Set(prev);
        next.add(parentId);
        return next;
      });
      try {
        const from = page * CHILD_PAGE_SIZE;
        const to = from + CHILD_PAGE_SIZE - 1;
        const { data, error, count } = await supabase
          .from("scientific_names")
          .select(BASE_SELECT, { count: "exact" })
          .eq("current_parent", parentId)
          .order("name_spell_valid", { ascending: true })
          .range(from, to);
        if (error) throw error;
        mergeNodes(data || []);
        mergeAuthorsFromRows(data || []);
        await ensureDescendantCounts([
          parentId,
          ...(data || []).map((d) => d.id).filter(Boolean),
        ]);
        setFetchedChildren((prev) => {
          const next = new Map(prev);
          const prevPage = next.get(parentId) ?? -1;
          if (page > prevPage) next.set(parentId, page);
          return next;
        });
        setChildrenTotals((prev) => ({
          ...prev,
          [parentId]: typeof count === "number" ? count : Math.max(prev[parentId] || 0, (data || []).length),
        }));
      } catch (err) {
        console.error("[Taxonomy] fetchChildren error:", err);
      } finally {
        setLoadingChildren((prev) => {
          const next = new Set(prev);
          next.delete(parentId);
          return next;
        });
      }
    },
    [fetchedChildren, mergeNodes, ensureDescendantCounts]
  );

  const loadNodeWithAncestors = useCallback(
    async (id) => {
      if (!id) return null;
      let node = allScientificNames.find((n) => n.id === id);
      if (!node) {
        node = await fetchNodeById(id);
      }
      if (!node) return null;

      const ancestorIds = [];
      let cur = node;
      const visited = new Set([cur.id]);
      while (cur?.current_parent && !visited.has(cur.current_parent)) {
        visited.add(cur.current_parent);
        let parent = allScientificNames.find((n) => n.id === cur.current_parent);
        if (!parent) {
          parent = await fetchNodeById(cur.current_parent);
        }
        if (!parent) break;
        ancestorIds.unshift(parent.id);
        cur = parent;
      }

      await fetchAuthorsForIds([node.id, ...ancestorIds]);
      await ensureDescendantCounts([node.id, ...ancestorIds]);
      // 祖先と対象ノードの子を事前ロード（最小限）
      for (const pid of [node.id, ...ancestorIds]) {
        await fetchChildren(pid);
      }

      const latest = allScientificNames.find((n) => n.id === id) || node;
      setSelectedScientificName(latest);
      setIsInitialLoad(false);
      return latest;
    },
    [allScientificNames, fetchChildren, fetchAuthorsForIds, fetchNodeById, ensureDescendantCounts]
  );

  // 検索サジェスト（サーバーサイド検索） デバウンス + キャンセル
  useDebouncedSearch({
    value: searchInput,
    delay: 400,
    onSearch: async () => {
      const q = (searchInput || "").trim();
      if (q.length < 2) {
        setSuggestionLoading(false);
        setSearchOptions(selectedScientificName ? [selectedScientificName] : []);
        return;
      }
      setSuggestionLoading(true);
      try {
        const { data, error } = await supabase
          .from("scientific_names")
          .select(BASE_SELECT)
          .or(`name_spell_valid.ilike.%${q}%,name_spell_original.ilike.%${q}%`)
          .order("name_spell_valid", { ascending: true })
          .limit(30);
        if (error) throw error;
        mergeNodes(data || []);
        mergeAuthorsFromRows(data || []);
        setSearchOptions(data || []);
      } catch (err) {
        console.error("[Taxonomy] search error:", err);
      } finally {
        setSuggestionLoading(false);
      }
    },
  });

  // 初期データ: Superfamily を1件取得して初期表示
  useEffect(() => {
    let mounted = true;
    if (!mounted) return;
    (async () => {
      try {
        setInitializing(true);
        setInitialLoadError(null);
        const { data, error } = await supabase
          .from("scientific_names")
          .select(BASE_SELECT)
          .eq("current_rank", "Superfamily")
          .ilike("name_spell_valid", "%Ichneumonoidea%")
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        if (data) {
          mergeNodes([data]);
          mergeAuthorsFromRows([data]);
          const loaded = await loadNodeWithAncestors(data.id);
          if (loaded) {
            setSearchOptions([loaded]);
            // ルートは初期から展開状態にしておく
            setExpandedNodes((prev) => ({ ...prev, [loaded.id]: true }));
            setPersistExpanded(new Set([loaded.id]));
          }
        }
      } catch (err) {
        console.error("[Taxonomy] initial load error", err);
        setInitialLoadError(err.message || "Failed to load taxonomy data.");
      } finally {
        setInitializing(false);
      }
    })();
    return () => {
      mounted = false;
    };
  // run once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // サーチ選択
  const handleSearchChange = useCallback((_, newValue) => {
    if (!newValue) return;
    loadNodeWithAncestors(newValue.id || newValue.value || newValue);
  }, [loadNodeWithAncestors]);

  // ノードクリック
  const handleOpenDialog = useCallback(async (node) => {
    if (!node?.id) return;
    setDialogOpen(true);
    setDialogData(null);
    try {
      const { data, error } = await supabase
        .from("scientific_names")
        .select("*")
        .eq("id", node.id)
        .maybeSingle();
      if (error) throw error;
      setDialogData(data || node);
    } catch (err) {
      console.error("[Taxonomy] failed to load detail", err);
      setDialogData(node);
    }
  }, []);
  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setDialogData(null);
  }, []);

  // 自動スクロール
  useEffect(() => {
    if (
      !initializing &&
      !isInitialLoad &&
      selectedScientificName &&
      resultsContainerRef.current
    ) {
      resultsContainerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [selectedScientificName, initializing, isInitialLoad]);

  // ローディング or エラー
  if (initializing) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            flexDirection: "column",
          }}
        >
          <CircularProgress color="primary" size={60} />
          <Typography sx={{ mt: 2, color: "text.secondary" }}>
            Loading Taxonomic Data...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }
  if (initialLoadError) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Container maxWidth="sm" sx={{ py: 5, textAlign: "center" }}>
          <Alert severity="error" icon={<ErrorOutlineIcon fontSize="inherit" />}>
            <AlertTitle>Error Loading Data</AlertTitle>
            {initialLoadError}
          </Alert>
        </Container>
      </ThemeProvider>
    );
  }

  // 本体レンダリング
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* ヘッダー */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{ mb: 5 }}
        >
          <ScienceIcon sx={{ fontSize: "2.5rem", color: "primary.main" }} />
          <Typography variant="h3">Taxonomy Explorer</Typography>
        </Stack>

        {/* 検索バー */}
        <Paper
          elevation={3}
          sx={{
            p: 2,
            mb: 4,
            position: "sticky",
            top: 10,
            zIndex: 1100,
            backgroundColor: "rgba(26,26,26,0.9)",
          }}
        >
          <Stack direction="row" justifyContent="center">
            <Autocomplete
              sx={{ width: { xs: "100%", sm: 500, md: 600 } }}
              options={searchOptions}
              loading={suggestionLoading}
              value={selectedScientificName || null}
              onChange={handleSearchChange}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              getOptionLabel={(opt) => opt?.name_spell_valid || ""}
              onInputChange={(_, v) => setSearchInput(v || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search or Select Taxon"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{ color: "primary.main", mr: 0.5 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Stack>
        </Paper>

        {/* 分類ツリー */}
        <Box ref={resultsContainerRef} sx={{ pt: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 2 }}
          >
            <AccountTreeIcon sx={{ color: "primary.main" }} />
            <Typography variant="h6">Taxonomic Tree</Typography>
          </Stack>
          <Divider sx={{ mb: 3 }} />

          {selectedScientificName ? (
            <Paper
              elevation={2}
              sx={{
                p: { xs: 1, sm: 2 },
                overflow: "auto",
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            >
              <TaxonomicTree
                rootScientificName={selectedScientificName}
                allScientificNames={allScientificNames}
                scientificNameAuthors={scientificNameAuthors}
                descendantCountMap={descendantCountMap}
                highlightId={selectedScientificName.id}
                onSelectScientificName={handleOpenDialog}
                onFetchChildren={fetchChildren}
                fetchedChildren={fetchedChildren}
                loadingChildren={loadingChildren}
                expandedNodes={expandedNodes}
                setExpandedNodes={setExpandedNodes}
                persistExpanded={persistExpanded}
                setPersistExpanded={setPersistExpanded}
                childrenTotals={childrenTotals}
                childPageSize={CHILD_PAGE_SIZE}
              />
            </Paper>
          ) : (
            <Typography
              color="text.secondary"
              textAlign="center"
              sx={{ my: 5 }}
            >
              No taxon selected. Please use the search bar above.
            </Typography>
          )}
        </Box>
      </Container>

      {/* 詳細ダイアログ */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="lg"
        scroll="paper"
      >
        <DialogTitle
          sx={{
            bgcolor: "background.paper",
            borderBottom: "1px solid rgba(127,255,212,0.2)",
          }}
        >
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h6">Scientific Name Details</Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          {dialogData ? (
            <DialogDisplayScientificNameDetails
              initialScientificName={dialogData}
              allScientificNames={allScientificNames}
              scientificNameAuthors={scientificNameAuthors}
              allTaxonomicActs={[]}
              allReferences={{ data: [] }}
            />
          ) : (
            <Typography color="text.secondary" align="center">
              No data to display.
            </Typography>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            bgcolor: "background.paper",
            borderTop: "1px solid rgba(127,255,212,0.2)",
          }}
        >
          <Button onClick={handleCloseDialog} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
